import * as React from 'react';
import { gql } from '@soundxyz/gql-string';
import { LandingRankedListItem } from '@components/Landing/shared/LandingRankedList/LandingRankedListItem';
import { useLikePlaylist } from '@components/Likes/useLikePlaylist';
import { CoverImages } from '@components/Playlists/basics/CoverImages';
import { AddToQueueMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToQueueMenuItem';
import { CopyLinkMenuItem } from '@components/basics/Dropdowns/MenuItems/CopyLinkMenuItem';
import { DeleteShelfMenuItem } from '@components/basics/Dropdowns/MenuItems/DeleteShelfMenuItem';
import { EditShelfMenuItem } from '@components/basics/Dropdowns/MenuItems/EditShelfMenuItem';
import { ShareMenuItem } from '@components/basics/Dropdowns/MenuItems/ShareMenuItem';
import { DeleteShelfModal } from '@components/modals/DeleteShelfModal';
import { MintsDrivenModal } from '@components/modals/MintsDrivenModal';
import { useAuth } from '@context/auth/base';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext } from '@context/toast';
import {
  getFragment,
  PlaylistPreviewDropdownInfoFragmentDoc,
  ViralPlaylistsRankedListItemFragmentDoc,
} from '@graphql/reactQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { usePlaylistPlayingState } from '@hooks/usePlaylistPlayingState';
import { trackClickEvent } from '@lib/analytics';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ViralPlaylistsRankedListItem on PlaylistChartRank {
    trendingIndicator
    currentRank
    id
    playlist {
      id
      webappUri
      name
      numReferralPurchases
      coverImage {
        id
        url
      }
      trackIds
      user {
        id
        username
        webappUri
      }
      type
      coverReleases {
        id
        coverImage {
          id
          url
        }
        staticCoverImage {
          id
          url
        }
      }
      ...PlaylistPreviewDropdownInfo
      ...PlaylistCoverImages
    }
  }
`);

interface Props {
  chartRankFrag: FragmentType<ViralPlaylistsRankedListItemFragmentDoc>;
}

export const ViralPlaylistsRankedListItem = ({ chartRankFrag }: Props) => {
  const chartRank = getFragment(ViralPlaylistsRankedListItemFragmentDoc, chartRankFrag);
  const { playlist } = chartRank;

  const { isPlaying, onPlayButtonClick } = usePlaylistPlayingState({
    playlistId: playlist.id,
  });
  const { authUser } = useAuth();
  const isOwner = authUser?.id === playlist.user.id;
  const isUserCreated = playlist.type === 'DEFAULT';
  const playlistInfo = getFragment(PlaylistPreviewDropdownInfoFragmentDoc, playlist);

  const { openToast } = ToastContext.useContainer();
  const { openModal } = ModalContext.useContainer();

  const { isLiked, onClick: onLikeClick } = useLikePlaylist({
    id: playlist.id,
    source: 'Landing Page Viral Playlists Hover',
  });

  return (
    <LandingRankedListItem
      title={{
        text: playlist.name,
        href: webappUriPath(playlist.webappUri),
      }}
      subtitle={{
        text: playlist.user.username,
        href: webappUriPath(playlist.user.webappUri),
      }}
      secondarySubtitle={{
        text: `${playlist.numReferralPurchases} mints driven`,
        onClick: () => {
          openModal(ModalType.MINTS_DRIVEN, {
            body: <MintsDrivenModal playlistId={playlist.id} />,
          });
        },
      }}
      imageRadius="rounded-md"
      id={chartRank.id}
      trendingIndicator={chartRank.trendingIndicator}
      rank={chartRank.currentRank}
      image={<CoverImages playlist={playlist} maxSize={56} priority={false} />}
      isPlaying={isPlaying}
      onClickCell={onPlayButtonClick}
      likeButton={{
        liked: isLiked,
        onClick: onLikeClick,
      }}
      dropdownContent={
        <>
          <AddToQueueMenuItem
            trackIds={playlist.trackIds}
            disabled={!playlist.trackIds.length}
            emitAnalytics={() => {
              trackClickEvent({
                event: 'Add to Queue Clicked',
                properties: {
                  associationId: playlist.id,
                  associationType: 'Playlist',
                  sourceComponent: 'Playlist Preview',
                },
              });
            }}
          />
          <CopyLinkMenuItem
            onClick={() => {
              navigator.clipboard
                .writeText(playlist.webappUri)
                .then(() => {
                  openToast({
                    text: 'Copied to clipboard',
                  });
                })
                .catch(() => {
                  openToast({
                    text: 'Failed to copy to clipboard',
                    variant: 'error',
                  });
                });
            }}
          />
          <ShareMenuItem playlist={playlistInfo} source="Playlist Preview" />
          {isOwner && (
            <>
              {isUserCreated && (
                <EditShelfMenuItem label="Edit Playlist" shelfWebappUri={playlist.webappUri} />
              )}
              <DeleteShelfMenuItem
                onClick={() =>
                  openModal(ModalType.DELETE_SHELF, {
                    body: <DeleteShelfModal shelfId={playlist.id} />,
                  })
                }
              />
            </>
          )}
        </>
      }
    />
  );
};
